import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { authApi, useAuth, formatApiErrorDetail } from "@/lib/auth";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ef_token";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  won: "Won",
  lost: "Lost",
};
const STATUS_STYLES = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  qualified: "bg-red-500/15 text-red-300 border-red-500/30",
  won: "bg-red-400/25 text-red-200 border-red-400/50",
  lost: "bg-red-500/10 text-red-300 border-red-500/30",
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  async function handleExportCsv() {
    setExporting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/leads/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `emergent-food-leads-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      try {
        const { data } = await authApi.get("/leads");
        setLeads(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoadingLeads(false);
      }
    })();
  }, [loading, user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  async function updateStatus(id, status) {
    try {
      const { data } = await authApi.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: data.status } : l)));
      toast.success(`Marked ${STATUS_LABELS[status]}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const filtered = leads.filter((l) => {
    if (filter !== "all" && (l.status || "new") !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        (l.company || "").toLowerCase().includes(s) ||
        (l.name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: leads.filter((l) => (l.status || "new") === s).length }),
    { all: leads.length }
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white grain relative" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_16px_rgba(239, 68, 68,0.8)]" />
            <span className="font-display font-black uppercase tracking-tight text-[16px]">
              Emergent<span className="text-red-400">.</span>Food
            </span>
            <span className="ml-2 px-2 py-0.5 rounded-full bg-white/5 text-[10px] uppercase tracking-[0.2em] text-white/60 font-mono-ef">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-white/50 font-mono-ef">{user.email}</span>
            <button
              onClick={handleExportCsv}
              disabled={exporting || leads.length === 0}
              data-testid="admin-export-csv"
              className="btn-pill btn-ghost text-xs disabled:opacity-40"
              title={leads.length === 0 ? "No leads to export" : "Export all leads as CSV"}
            >
              {exporting ? "Exporting…" : "Export CSV"}
              <svg className="ml-2" width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v14M6 13l6 6 6-6M4 21h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              onClick={() => { toast.success("Signed out"); logout(); }}
              data-testid="admin-logout"
              className="btn-pill btn-ghost text-xs"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-red-400 mb-4">
            — Leads
          </div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-[-0.035em] leading-none">
            Pipeline.
          </h1>
          <p className="mt-4 text-white/55 max-w-xl">
            {leads.length} total leads · sorted by most recent.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          <div className="flex flex-wrap items-center gap-2" data-testid="status-filter">
            <button
              onClick={() => setFilter("all")}
              data-testid="filter-all"
              className={`px-3 py-1.5 rounded-full border text-xs font-mono-ef uppercase tracking-[0.15em] transition-colors ${
                filter === "all" ? "border-white text-white bg-white/10" : "border-white/15 text-white/60 hover:text-white"
              }`}
            >
              All · {counts.all}
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-1.5 rounded-full border text-xs font-mono-ef uppercase tracking-[0.15em] transition-colors ${
                  filter === s ? "border-red-400 text-red-300 bg-red-500/10" : "border-white/15 text-white/60 hover:text-white"
                }`}
              >
                {STATUS_LABELS[s]} · {counts[s]}
              </button>
            ))}
          </div>
          <div className="lg:ml-auto flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 min-w-[280px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white/40">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, name or email…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-10 rounded-2xl border border-white/10 overflow-hidden bg-[#0a0a0c]" data-testid="leads-table">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono-ef border-b border-white/10 bg-white/[0.02]">
            <div className="col-span-3">Company</div>
            <div className="col-span-2 hidden lg:block">Contact</div>
            <div className="col-span-3">Project</div>
            <div className="col-span-2 hidden lg:block">Received</div>
            <div className="col-span-2">Status</div>
          </div>

          {loadingLeads ? (
            <div className="p-10 text-center text-white/40 text-sm" data-testid="leads-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-white/40" data-testid="leads-empty">
              No leads match this filter.
            </div>
          ) : (
            filtered.map((l) => (
              <div
                key={l.id}
                className="grid grid-cols-12 gap-4 px-6 py-6 border-t border-white/5 hover:bg-white/[0.02] transition-colors items-start"
                data-testid={`lead-row-${l.id}`}
              >
                <div className="col-span-12 lg:col-span-3">
                  <div className="font-display font-bold text-lg text-white">{l.company}</div>
                  <div className="text-xs text-white/50 mt-1 lg:hidden">{l.email}</div>
                </div>
                <div className="col-span-6 lg:col-span-2 hidden lg:block">
                  <div className="text-sm text-white/80">{l.name}</div>
                  <div className="text-xs text-white/40 mt-1 truncate">{l.email}</div>
                  {l.country && <div className="text-xs text-white/40 mt-0.5">{l.country}</div>}
                </div>
                <div className="col-span-12 lg:col-span-3">
                  <div className="flex flex-wrap gap-1.5">
                    {[l.project_type, l.product_category, l.volume, l.timeline]
                      .filter(Boolean)
                      .map((v) => (
                        <span key={v} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.14em] text-white/60 font-mono-ef">
                          {v}
                        </span>
                      ))}
                  </div>
                  {l.message && (
                    <p className="mt-2 text-xs text-white/45 leading-relaxed line-clamp-2">
                      {l.message}
                    </p>
                  )}
                  {(l.utm_source || l.utm_campaign || l.utm_medium) && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`lead-utm-${l.id}`}>
                      {l.utm_source && (
                        <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-[10px] uppercase tracking-[0.14em] text-red-300 font-mono-ef">
                          src: {l.utm_source}
                        </span>
                      )}
                      {l.utm_medium && (
                        <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] uppercase tracking-[0.14em] text-cyan-300 font-mono-ef">
                          med: {l.utm_medium}
                        </span>
                      )}
                      {l.utm_campaign && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] uppercase tracking-[0.14em] text-amber-300 font-mono-ef">
                          camp: {l.utm_campaign}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-6 lg:col-span-2 hidden lg:block text-xs text-white/40 font-mono-ef">
                  {formatDate(l.created_at)}
                  <div className="mt-1 text-white/30">via {l.source || "landing"}</div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.16em] font-mono-ef ${STATUS_STYLES[l.status || "new"]}`}
                    >
                      {STATUS_LABELS[l.status || "new"]}
                    </span>
                    <select
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      data-testid={`status-select-${l.id}`}
                      className="text-xs bg-transparent border border-white/15 rounded-full px-2 py-1 outline-none hover:border-red-400 transition-colors"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0a0a0c]">
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
